// Guia do dia (item 67) e página do limite (item 69): o que decide se a folha "Hoje você tem 2 partidas grátis" abre,
// o que ela diz e a conta do ranking da página "Você já jogou suas 2 partidas grátis de hoje". Separado dos componentes
// para os testes rodarem sem o navegador.
import { type Plano } from '@truco6/planos';
import { VALORES_PADRAO, type LinhaRanking, type RankingNaTela } from '@truco6/gamificacao';
import type { SituacaoLimite } from './api.ts';
import { diariasRestantes, noGratis, semPartidas } from './limite.ts';

// O dia de São Paulo (AAAA-MM-DD): a folha abre uma vez por conta e por dia.
const DIA = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit' });
export const diaEmSaoPaulo = (agora: number) => DIA.format(new Date(agora));
const PREFIXO = 't6:guia-do-dia:';
export const chaveDoGuia = (conta: string, agora: number) => `${PREFIXO}${conta}:${diaEmSaoPaulo(agora)}`;

// Sem armazenamento (aba anônima, dados bloqueados), a folha abre uma vez por sessão.
const vistosNaSessao = new Set<string>();

export function jaViuHoje(conta: string, agora: number): boolean {
  const k = chaveDoGuia(conta, agora);
  if (vistosNaSessao.has(k)) return true;
  try { return localStorage.getItem(k) !== null; } catch { return false; }
}

// Marca o dia como visto. A página do limite usa a mesma chave: quem viu a página não vê a folha no mesmo dia.
export function marcarGuiaVisto(conta: string, agora: number): void {
  const k = chaveDoGuia(conta, agora);
  vistosNaSessao.add(k);
  try {
    // Os dias anteriores da mesma conta saem, para a chave não acumular.
    const antigas: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const x = localStorage.key(i);
      if (x && x !== k && x.startsWith(`${PREFIXO}${conta}:`)) antigas.push(x);
    }
    for (const x of antigas) localStorage.removeItem(x);
    localStorage.setItem(k, '1');
  } catch { /* sem armazenamento: vale a sessão */ }
}

// Conta nova: o Criar perfil marca, e a primeira folha diz "Bem-vindo à mesa!". Fica só na memória da página.
let contaNova = false;
export const marcarContaNova = () => { contaNova = true; };
export function tirarContaNova(): boolean {
  const era = contaNova;
  contaNova = false;
  return era;
}

// Quando a aba Jogar abre: null enquanto o plano e o limite não chegaram; 'guia' abre a folha; 'limite' leva à página
// do limite (Grátis sem partidas do dia e sem bônus); 'nada' para quem assina ou já viu hoje.
export type DecisaoDoGuia = 'nada' | 'guia' | 'limite';
export function decidirGuia(plano: Plano | null, l: SituacaoLimite | null, conta: string, agora: number): DecisaoDoGuia | null {
  if (!plano || !l) return null;
  if (!noGratis(plano, l) || l.restantes === null) return 'nada';
  if (jaViuHoje(conta, agora)) return 'nada';
  return semPartidas(l) ? 'limite' : 'guia';
}

const partidas = (n: number, tipo: 'grátis' | 'bônus') => `${n} ${n === 1 ? 'partida' : 'partidas'} ${tipo}`;

// O título da folha e o chip do bônus, conforme as partidas que sobram hoje.
export function textosDoGuia(l: SituacaoLimite, nova = false): { titulo: string; bonus: number | null } {
  const total = l.limite ?? 0, diarias = diariasRestantes(l), bonus = l.bonus;
  let titulo: string;
  if (nova && diarias === total) titulo = `Bem-vindo à mesa! Você tem ${partidas(total, 'grátis')} por dia`;
  else if (diarias === 0) titulo = `As grátis de hoje acabaram. Você tem ${partidas(bonus, 'bônus')}`;
  else if (diarias === total) titulo = `Hoje você tem ${partidas(diarias, 'grátis')}`;
  else titulo = `Hoje sobra ${partidas(diarias, 'grátis')}`;
  return { titulo, bonus: diarias > 0 && bonus > 0 ? bonus : null };
}

// A meia-noite seguinte em São Paulo (UTC-3, sem horário de verão desde 2019).
export function proximaMeiaNoite(agora: number): number {
  const d = new Date(agora - 3 * 3_600_000);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1) + 3 * 3_600_000;
}

// "Voltam em 5 h 42 min", contado a partir de voltaEm (a meia-noite de São Paulo que o /api/eu manda).
export function tempoAteVoltar(voltaEm: number, agora: number): string {
  const ate = voltaEm > agora ? voltaEm : proximaMeiaNoite(agora);
  const min = Math.max(1, Math.ceil((ate - agora) / 60_000));
  const h = Math.floor(min / 60), m = min % 60;
  return h ? (m ? `Voltam em ${h} h ${m} min` : `Voltam em ${h} h`) : `Voltam em ${m} min`;
}

// O cartão do ranking da semana na página do limite: a posição de quem pede e a distância para o top 10.
// Pontos que faltam = pontos do 10º menos os seus, mais 1. Vitórias = pontos que faltam divididos pela vitória do
// ranking, para cima.
export const PONTOS_DA_VITORIA = VALORES_PADRAO.liga_vitoria;
export type SituacaoNoRanking =
  | { tipo: 'sem_pontos' }
  | { tipo: 'top10'; eu: LinhaRanking }
  | { tipo: 'fora'; eu: LinhaRanking; decimo: number; faltam: number; vitorias: number };
export function situacaoNoRanking(r: Pick<RankingNaTela, 'linhas' | 'eu'>): SituacaoNoRanking {
  const eu = r.eu;
  if (!eu || eu.pontos <= 0) return { tipo: 'sem_pontos' };
  if (eu.posicao > 0 && eu.posicao <= 10) return { tipo: 'top10', eu };
  const decimo = r.linhas[9]?.pontos ?? 0;
  const faltam = Math.max(1, decimo - eu.pontos + 1);
  return { tipo: 'fora', eu, decimo, faltam, vitorias: Math.ceil(faltam / PONTOS_DA_VITORIA) };
}

// O nome do lugar no rótulo: "Carapicuíba · SP" vira "CARAPICUÍBA"; sem lugar, o Brasil.
export const lugarDoRanking = (r: Pick<RankingNaTela, 'escopo' | 'lugar'>) =>
  (r.escopo === 'brasil' || !r.lugar ? 'Brasil' : r.lugar.split(' · ')[0]!).toLocaleUpperCase('pt-BR');
